import { useCallback, useEffect, useMemo, useState } from "react";
import { ClipLoader } from "react-spinners";
import {
  MagnifyingGlassIcon,
  ArrowPathIcon,
} from "@heroicons/react/24/outline";
import axiosClient from "../../axiosClient";
import PageHeader from "../../components/PageHeader/PageHeader";
import BasicSelect from "../../components/BasicSelect/BasicSelect";

interface UserMetric {
  id: string;
  email: string | null;
  name: string | null;
  role: string;
  workouts: number;
  exercises: number;
  programs: number;
  workoutTemplates: number;
  lastWorkout: string | null;
  lastExercise: string | null;
  lastActive: string | null;
  workoutsLastCreated: string | null;
  workoutsLastUpdated: string | null;
  exercisesLastCreated: string | null;
  exercisesLastUpdated: string | null;
}

interface Overview {
  totalUsers: number;
  totalWorkouts: number;
  totalExercises: number;
  totalPrograms: number;
  totalWorkoutTemplates: number;
  activeUsers30d: number;
  activeWindowDays: number;
}

interface MetricsResponse {
  overview: Overview;
  users: UserMetric[];
}

type SortKey = "active" | "workouts" | "exercises" | "programs" | "name";

const SORT_OPTIONS = [
  { value: "active", label: "Recently active" },
  { value: "workouts", label: "Most workouts" },
  { value: "exercises", label: "Most exercises" },
  { value: "programs", label: "Most programs" },
  { value: "name", label: "Name (A–Z)" },
];

const ACTIVE_WINDOW_MS = 30 * 24 * 60 * 60 * 1000;

const isRecentlyActive = (iso: string | null): boolean => {
  if (!iso) return false;
  const time = new Date(iso).getTime();
  return !Number.isNaN(time) && Date.now() - time < ACTIVE_WINDOW_MS;
};

const formatDate = (iso: string | null): string => {
  if (!iso) return "—";
  const time = new Date(iso).getTime();
  if (Number.isNaN(time)) return "—";
  const diffDays = Math.floor((Date.now() - time) / (1000 * 60 * 60 * 24));
  if (diffDays <= 0) return "Today";
  if (diffDays === 1) return "Yesterday";
  if (diffDays < 30) return `${diffDays}d ago`;
  return new Date(time).toLocaleDateString(undefined, {
    month: "short",
    day: "numeric",
    year: "numeric",
  });
};

// Absolute date + time, for the created/updated timestamps where the actual
// time-of-day is meaningful.
const formatDateTime = (iso: string | null): string => {
  if (!iso) return "—";
  const time = new Date(iso).getTime();
  if (Number.isNaN(time)) return "—";
  return new Date(time).toLocaleString(undefined, {
    month: "short",
    day: "numeric",
    hour: "numeric",
    minute: "2-digit",
  });
};

const UserMetrics = () => {
  const [data, setData] = useState<MetricsResponse | null>(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [search, setSearch] = useState("");
  const [sortKey, setSortKey] = useState<SortKey>("active");

  const loadMetrics = useCallback(async () => {
    setLoading(true);
    setError(null);
    try {
      const res = await axiosClient.get<MetricsResponse>(
        "/api/admin/user-metrics"
      );
      setData(res.data);
    } catch (e) {
      const err = e as {
        response?: { status?: number; data?: { message?: string } };
      };
      if (err.response?.status === 403) {
        setError("You don't have admin access to view this page.");
      } else {
        setError(
          err.response?.data?.message ||
            "Failed to load user metrics. Please try again."
        );
      }
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => {
    loadMetrics();
  }, [loadMetrics]);

  const visibleUsers = useMemo(() => {
    if (!data) return [];
    const query = search.trim().toLowerCase();
    const filtered = query
      ? data.users.filter(
          (u) =>
            (u.name || "").toLowerCase().includes(query) ||
            (u.email || "").toLowerCase().includes(query)
        )
      : data.users;

    const sorted = [...filtered];
    const lastActiveTime = (u: UserMetric) =>
      u.lastActive ? new Date(u.lastActive).getTime() : 0;

    switch (sortKey) {
      case "workouts":
        sorted.sort((a, b) => b.workouts - a.workouts);
        break;
      case "exercises":
        sorted.sort((a, b) => b.exercises - a.exercises);
        break;
      case "programs":
        sorted.sort((a, b) => b.programs - a.programs);
        break;
      case "name":
        sorted.sort((a, b) =>
          (a.name || a.email || "").localeCompare(b.name || b.email || "")
        );
        break;
      case "active":
      default:
        sorted.sort((a, b) => lastActiveTime(b) - lastActiveTime(a));
        break;
    }
    return sorted;
  }, [data, search, sortKey]);

  const overviewCards = data
    ? [
        { label: "Users", value: data.overview.totalUsers },
        {
          label: `Active (${data.overview.activeWindowDays}d)`,
          value: data.overview.activeUsers30d,
        },
        { label: "Workouts", value: data.overview.totalWorkouts },
        { label: "Exercises", value: data.overview.totalExercises },
        { label: "Programs", value: data.overview.totalPrograms },
        { label: "Templates", value: data.overview.totalWorkoutTemplates },
      ]
    : [];

  return (
    <>
      <PageHeader backLink="/admin-dashboard" pageTitle="User Metrics" />

      <div className="h-[calc(100%-63px)] overflow-y-auto bg-gray-50/50 p-4 space-y-4">
        {loading && (
          <div className="flex justify-center py-16">
            <ClipLoader color="#4f46e5" speedMultiplier={0.7} />
          </div>
        )}

        {!loading && error && (
          <div className="rounded-md bg-red-50 border border-red-200 px-4 py-3 text-sm text-red-700">
            {error}
            <button
              type="button"
              onClick={loadMetrics}
              className="ml-2 font-medium underline hover:text-red-900"
            >
              Retry
            </button>
          </div>
        )}

        {!loading && !error && data && (
          <>
            {/* Platform overview */}
            <div className="grid grid-cols-2 sm:grid-cols-3 gap-3">
              {overviewCards.map((card) => (
                <div
                  key={card.label}
                  className="bg-white border border-gray-200 rounded-md p-3"
                >
                  <div className="text-2xl font-bold text-gray-900">
                    {card.value}
                  </div>
                  <div className="mt-0.5 text-xs text-gray-500">
                    {card.label}
                  </div>
                </div>
              ))}
            </div>

            {/* Controls */}
            <div className="space-y-2">
              <div className="relative">
                <MagnifyingGlassIcon className="pointer-events-none absolute left-3 top-1/2 h-4 w-4 -translate-y-1/2 text-gray-400" />
                <input
                  type="text"
                  value={search}
                  onChange={(e) => setSearch(e.target.value)}
                  placeholder="Search by name or email"
                  spellCheck={false}
                  autoCapitalize="off"
                  autoCorrect="off"
                  className="block w-full rounded-md border-gray-300 pl-9 shadow-sm focus:border-indigo-500 focus:ring-indigo-500 text-sm"
                />
              </div>
              <div className="flex items-center gap-2">
                <BasicSelect
                  id="user-metrics-sort"
                  className="flex-1"
                  initialSelectedOption={sortKey}
                  options={SORT_OPTIONS}
                  onSelect={(option) => setSortKey(option.value as SortKey)}
                />
                <button
                  type="button"
                  onClick={loadMetrics}
                  aria-label="Refresh"
                  className="flex h-[42px] w-[42px] shrink-0 items-center justify-center rounded-md border border-gray-300 bg-white text-gray-500 shadow-sm hover:bg-gray-50 active:bg-gray-100"
                >
                  <ArrowPathIcon className="h-5 w-5" />
                </button>
              </div>
              <div className="text-xs text-gray-500">
                Showing {visibleUsers.length} of {data.users.length} users
              </div>
            </div>

            {/* User cards */}
            {visibleUsers.length === 0 ? (
              <div className="rounded-md border border-dashed border-gray-300 bg-white px-4 py-8 text-center text-sm text-gray-500">
                {data.users.length === 0
                  ? "No users yet."
                  : "No users match your search."}
              </div>
            ) : (
              <div className="space-y-3">
                {visibleUsers.map((u) => {
                  const active = isRecentlyActive(u.lastActive);
                  return (
                    <div
                      key={u.id}
                      className="bg-white border border-gray-200 rounded-md p-4"
                    >
                      <div className="flex items-center gap-2">
                        <span
                          className={`h-2 w-2 shrink-0 rounded-full ${
                            active ? "bg-green-500" : "bg-gray-300"
                          }`}
                          title={active ? "Active in last 30 days" : "Inactive"}
                        />
                        <span className="min-w-0 truncate font-semibold text-gray-900">
                          {u.name || u.email || u.id}
                        </span>
                        {u.role === "Admin" && (
                          <span className="shrink-0 rounded-full bg-indigo-100 px-2 py-0.5 text-[10px] font-semibold uppercase tracking-wide text-indigo-700">
                            Admin
                          </span>
                        )}
                      </div>
                      {u.email && (
                        <div className="mt-0.5 truncate text-xs text-gray-500">
                          {u.email}
                        </div>
                      )}

                      <div className="mt-3 grid grid-cols-4 gap-2 text-center">
                        {[
                          { label: "Workouts", value: u.workouts },
                          { label: "Exercises", value: u.exercises },
                          { label: "Programs", value: u.programs },
                          { label: "Templates", value: u.workoutTemplates },
                        ].map((stat) => (
                          <div
                            key={stat.label}
                            className="rounded-md bg-gray-50 py-2"
                          >
                            <div className="text-lg font-bold text-gray-900">
                              {stat.value}
                            </div>
                            <div className="text-[10px] uppercase tracking-wide text-gray-500">
                              {stat.label}
                            </div>
                          </div>
                        ))}
                      </div>

                      <div className="mt-3 grid grid-cols-2 gap-2 text-xs text-gray-500">
                        <div>
                          Last workout:{" "}
                          <span className="text-gray-700">
                            {formatDate(u.lastWorkout)}
                          </span>
                        </div>
                        <div>
                          Last exercise:{" "}
                          <span className="text-gray-700">
                            {formatDate(u.lastExercise)}
                          </span>
                        </div>
                      </div>

                      <div className="mt-3 grid grid-cols-2 gap-3 border-t border-gray-100 pt-3 text-xs">
                        <div>
                          <div className="mb-0.5 font-medium text-gray-500">
                            Workouts
                          </div>
                          <div className="text-gray-400">
                            Created{" "}
                            <span className="text-gray-700">
                              {formatDateTime(u.workoutsLastCreated)}
                            </span>
                          </div>
                          <div className="text-gray-400">
                            Updated{" "}
                            <span className="text-gray-700">
                              {formatDateTime(u.workoutsLastUpdated)}
                            </span>
                          </div>
                        </div>
                        <div>
                          <div className="mb-0.5 font-medium text-gray-500">
                            Exercises
                          </div>
                          <div className="text-gray-400">
                            Created{" "}
                            <span className="text-gray-700">
                              {formatDateTime(u.exercisesLastCreated)}
                            </span>
                          </div>
                          <div className="text-gray-400">
                            Updated{" "}
                            <span className="text-gray-700">
                              {formatDateTime(u.exercisesLastUpdated)}
                            </span>
                          </div>
                        </div>
                      </div>
                    </div>
                  );
                })}
              </div>
            )}
          </>
        )}
      </div>
    </>
  );
};

export default UserMetrics;
